/* eslint-disable no-use-before-define */
import { ApiCPPIStrategy, ApiTIPPStrategy, ApiUnapprovedAccount } from 'api/_legacy/generated-api'
import {
  ApiAccountBuilderState,
  ApiAccountState,
  ApiAssetWeight,
  ApiBusinessUnit,
  ApiFundOfFundsPortfolio,
  ApiModelPortfolio,
  ApiPassThroughStrategy,
  ApiPortfolioType,
  ApiSelfSelectPortfolio,
  ApiUnapprovedAccountUpdate,
  ApiVolControlStrategy
} from 'api/rtk-api'

import { handleDefaultRebalancingDefinition } from '../../../../../utilities'
import { TopupWithdrawStepState } from '../../../../trade-editor/modules/01-topup-withdraw/state/topup-withdraw-slice'
import { TopUpWithdrawState } from '../../../../trade-editor/types/TopUpWithdrawState'
import { getDateOnlyAsJson } from '../../../util/get-date-only-as-json'
import { convertFromPercentage } from '../../../util/percentage'
import { NoteStepState } from '../../01-note/note/note-step-slice'
import { FundingSource, TermsStepState } from '../../02-terms/state/terms-slice'
import { PortfolioStepState } from '../../03-portfolio/state/portfolio-slice'
import { StrategyState, StrategyStepState } from '../../04-strategy/state/strategy-slice'

function mapDelta1Strategy(): ApiPassThroughStrategy {
  return {
    type: 'PassThrough',
    leverageRatio: 1
  }
}

function mapRiskManagedStrategy(strategyStepState: StrategyStepState): ApiVolControlStrategy {
  return {
    type: 'VolControl',
    targetVolatility: convertFromPercentage(
      strategyStepState.selectedStrategy.riskManaged.targetVolatility
    ).value,
    leverageRatio: 1
  }
}

function mapLeverageStrategy(
  strategyStepState: StrategyStepState
): ApiVolControlStrategy | ApiPassThroughStrategy {
  if (strategyStepState.selectedStrategy.leverage.type === 'constant-proportion') {
    return {
      type: 'PassThrough',
      leverageRatio:
        strategyStepState.selectedStrategy.leverage.constantProportion.leverageRatio.value
    }
  }

  if (strategyStepState.selectedStrategy.leverage.type === 'vol-control') {
    return {
      type: 'VolControl',
      leverageRatio: strategyStepState.selectedStrategy.leverage.volControl.leverageRatio.value,
      targetVolatility: convertFromPercentage(
        strategyStepState.selectedStrategy.leverage.volControl.targetVolatility
      ).value
    }
  }
}

function mapTIPPStrategy(strategyStepState: StrategyStepState): ApiTIPPStrategy {
  const protectionStrategy = strategyStepState.selectedStrategy.protection
  const { continuous } = protectionStrategy

  return {
    type: 'TIPP',
    leverageRatio: continuous.leverageRatio.value,
    targetVolatility: protectionStrategy.isApplyVolControl
      ? convertFromPercentage(continuous.targetVolatility).value
      : undefined,
    protectionPercentage: convertFromPercentage(continuous.protectionLevel).value,
    hasBankGuarantee: strategyStepState.selectedStrategy.protection.isBankGuarantee,
    highWatermarkObservationFrequency: continuous.highWaterMarkFrequency
  }
}

function mapCPPIStrategy(strategyStepState: StrategyStepState): ApiCPPIStrategy {
  const { protection } = strategyStepState.selectedStrategy
  const { term } = protection
  return {
    type: 'CPPI',
    leverageRatio: term.leverageRatio.value,
    targetVolatility: protection.isApplyVolControl
      ? convertFromPercentage(term.targetVolatility).value
      : undefined,
    protectionPercentage: convertFromPercentage(term.protectionLevel).value,
    highWatermarkObservationFrequency: term.highWaterMarkFrequency,
    hasBankGuarantee: protection.isBankGuarantee,
    maturityDate: term.maturityDate.value
  }
}

export function mapStrategy(strategyStepState: StrategyStepState) {
  switch (strategyStepState.selectedStrategy.type) {
    case 'delta1':
      return mapDelta1Strategy()
    case 'riskmanaged':
      return mapRiskManagedStrategy(strategyStepState)
    case 'leverage':
      return mapLeverageStrategy(strategyStepState)
    case 'protection':
      switch (strategyStepState.selectedStrategy.protection.type) {
        case 'continuous':
          return mapTIPPStrategy(strategyStepState)
        case 'term':
          return mapCPPIStrategy(strategyStepState)
        default:
          return undefined
      }
    default:
      return undefined
  }
}

export function mapProtectionPortfolioWeights(strategyStepState: StrategyStepState) {
  if (!strategyStepState) {
    return undefined
  }

  switch (strategyStepState.selectedStrategy.type) {
    case 'riskmanaged':
      return [
        {
          assetId: strategyStepState.selectedStrategy.riskManaged.protectionAsset?.assetId,
          weight: 1
        }
      ]
    case 'leverage':
      if (strategyStepState.selectedStrategy.leverage.type === 'vol-control') {
        return [
          {
            assetId:
              strategyStepState.selectedStrategy.leverage.volControl.protectionAsset?.assetId,
            weight: 1
          }
        ]
      }
      return undefined

    case 'protection':
      if (strategyStepState.selectedStrategy.protection.type === 'continuous') {
        return [
          {
            assetId:
              strategyStepState.selectedStrategy.protection.continuous.protectionAsset?.assetId,
            weight: 1
          }
        ]
      }
      return [
        {
          assetId: strategyStepState.selectedStrategy.protection.term.protectionAsset?.assetId,
          weight: 1
        }
      ]
    case 'delta1':
      return []
    default:
      return undefined
  }
}

export function mapStrategyTemplateId(strategyStepState: StrategyStepState) {
  if (!strategyStepState.selectedStrategy.type) {
    return undefined
  }

  const strategyTemplate = strategyStepState.strategyTemplates.find(
    x => x.strategyName === strategyStepState.selectedStrategy.selectedStrategyName
  )

  return strategyTemplate.strategyTemplateId
}

function mapModelPortfolio(
  account: ApiUnapprovedAccount | ApiUnapprovedAccountUpdate,
  portfolioStepState: PortfolioStepState
) {
  account.portfolioId = portfolioStepState.model?.portfolio?.portfolioId
}

function mapSelfSelectPortfolio(
  account: ApiUnapprovedAccount | ApiUnapprovedAccountUpdate,
  portfolioStepState: PortfolioStepState
) {
  const portfolio: ApiSelfSelectPortfolio = {
    type: 'SelfSelect',
    rebalancingDefinition: handleDefaultRebalancingDefinition(
      portfolioStepState.selfSelect.portfolio.rebalancingDefinition
    ),
    targetWeights: portfolioStepState.selfSelect.portfolio.targetWeights,
    fees: []
  }
  if (portfolio.targetWeights.length > 0) {
    account.selfSelectPortfolio = portfolio
  }
}

function mapPortfolio(
  account: ApiUnapprovedAccount | ApiUnapprovedAccountUpdate,
  portfolioStepState: PortfolioStepState
) {
  switch (portfolioStepState.portfolioType) {
    case 'ModelPortfolio': {
      mapModelPortfolio(account, portfolioStepState)
      break
    }
    case 'FundOfFunds': {
      mapModelPortfolio(account, portfolioStepState)
      break
    }
    case 'SelfSelect': {
      mapSelfSelectPortfolio(account, portfolioStepState)
    }
  }
}

export function mapStateToAccount(
  accountId: string,
  note: NoteStepState,
  terms: TermsStepState,
  portfolio: PortfolioStepState,
  strategy: StrategyStepState
): ApiUnapprovedAccount {
  let account: ApiUnapprovedAccount = {
    accountId,
    accountName: terms.terms?.accountName,
    requestedStartDate: getDateOnlyAsJson(terms.terms?.requestedStartDate),
    noteId: note.note?.noteId,
    strategy: mapStrategy(strategy),
    strategyTemplateId: strategy.selectedStrategy.id,
    protectionPortfolioWeights: mapProtectionPortfolioWeights(strategy)
  }

  switch (terms.terms.fundingSource) {
    case FundingSource.Transfer:
      account = {
        ...account,
        transferUnits: portfolio.selfSelect ? portfolio.selfSelect.transferUnits : []
      }
      break
    case FundingSource.Cash:
      account = {
        ...account,
        investment: Number(terms.terms?.investment)
      }
      break
    case FundingSource.Switch:
      account = {
        ...account,
        switchRequest: {
          originatingAccountId: terms.terms.switchTrade.accountId,
          isFullSwitch: terms.terms.fullSwitch,
          switchAmount: terms.terms.fullSwitch ? null : Number(terms.terms.switchInvestment),
          isProcessed: null
        }
      }
      break
  }

  mapPortfolio(account, portfolio)

  return account
}

export function mapStateToAccountUpdate(
  accountId: string,
  topupWithdraw: TopupWithdrawStepState,
  portfolio: PortfolioStepState,
  strategy: StrategyStepState
): ApiUnapprovedAccountUpdate {
  const account: ApiUnapprovedAccountUpdate = {
    accountId
  }
  if (topupWithdraw.topUpWithrawState === TopUpWithdrawState.Topup) {
    account.depositValue = topupWithdraw.changeAmount
  }
  if (topupWithdraw.topUpWithrawState === TopUpWithdrawState.Withdraw) {
    account.withdrawalValue = topupWithdraw.changeAmount
  }

  if (isPortfolioEdited(portfolio)) {
    mapPortfolio(account, portfolio)
  }

  if (isStrategyEdited(strategy)) {
    account.strategy = mapStrategy(strategy)
    account.strategyTemplateId = strategy.selectedStrategy.id
    account.protectionPortfolioWeights = mapProtectionPortfolioWeights(strategy)
  }

  return account
}

function isPortfolioEdited(portfolioStepState: PortfolioStepState) {
  if (portfolioStepState.readonlyCurrentPortfolio) {
    if (
      portfolioStepState.readonlyCurrentPortfolio.portfolioType !== portfolioStepState.portfolioType
    )
      return true
    switch (portfolioStepState.portfolioType) {
      case 'ModelPortfolio': {
        const currentModelId = portfolioStepState.readonlyCurrentPortfolio.portfolioId
        const selectedModelId = portfolioStepState.model.portfolio.portfolioId
        const hasChanged = currentModelId !== selectedModelId
        return hasChanged
      }
      case 'SelfSelect': {
        const currentSelfSelect = {
          rebalancingDefinition: portfolioStepState.readonlyCurrentPortfolio.rebalDef,
          targetWeights: portfolioStepState.readonlyCurrentPortfolio.targetWeights
        }
        const selectedSelfSelect = {
          rebalancingDefinition: portfolioStepState.selfSelect.portfolio.rebalancingDefinition,
          targetWeights: portfolioStepState.selfSelect.portfolio.targetWeights
        }
        const isSame =
          currentSelfSelect.rebalancingDefinition.type ===
            selectedSelfSelect.rebalancingDefinition.type &&
          currentSelfSelect.rebalancingDefinition.frequency ===
            selectedSelfSelect.rebalancingDefinition.frequency &&
          currentSelfSelect.rebalancingDefinition.weightsTolerance ===
            selectedSelfSelect.rebalancingDefinition.weightsTolerance &&
          currentSelfSelect.targetWeights.length === selectedSelfSelect.targetWeights.length &&
          currentSelfSelect.targetWeights.every((asset, idx) =>
            isAssetWeightsSame(asset, selectedSelfSelect.targetWeights[idx])
          )
        return !isSame
      }
      default:
    }
  } else {
    return true
  }
}

function isAssetWeightsSame(lhs: ApiAssetWeight, rhs: ApiAssetWeight) {
  return lhs.asset.assetId === rhs.asset.assetId && lhs.weight === rhs.weight
}

function isStrategyEdited(strategyStepState: StrategyStepState) {
  if (strategyStepState.readonlyCurrentState) {
    const areEqual = areStrategiesSame(
      strategyStepState.readonlyCurrentState,
      strategyStepState.selectedStrategy
    )
    return !areEqual
  }
  return true
}

const areStrategiesSame = (lhs: StrategyState, rhs: StrategyState) => {
  const currentStrategyType = lhs.type
  const selectedStrategyType = rhs.type
  const isStrategyTypeSame = currentStrategyType === selectedStrategyType
  if (isStrategyTypeSame)
    // check if anything modified withint the same strategy
    switch (selectedStrategyType) {
      case 'delta1':
        return true
      case 'riskmanaged': {
        const lhsRiskManaged = lhs.riskManaged
        const rhsRiskManaged = rhs.riskManaged
        return (
          lhsRiskManaged.protectionAsset.assetId === rhsRiskManaged.protectionAsset.assetId &&
          lhsRiskManaged.targetVolatility?.value === rhsRiskManaged.targetVolatility?.value
        )
      }
      case 'leverage': {
        const lhsLeverage = lhs.leverage
        const rhsLeverage = rhs.leverage
        const sameLeverageType = lhsLeverage.type === rhsLeverage.type
        if (!sameLeverageType) return false

        switch (lhs.leverage.type) {
          case 'constant-proportion': {
            const lhsConstantProportion = lhs.leverage.constantProportion
            const rhsConstantProportion = rhs.leverage.constantProportion
            return (
              lhsConstantProportion.leverageRatio?.value ===
              rhsConstantProportion.leverageRatio?.value
            )
          }
          case 'vol-control': {
            const lhsVolControl = lhs.leverage.volControl
            const rhsVolControl = rhs.leverage.volControl
            return (
              lhsVolControl.protectionAsset?.assetId === rhsVolControl.protectionAsset?.assetId &&
              lhsVolControl.leverageRatio?.value === rhsVolControl.leverageRatio?.value &&
              lhsVolControl.targetVolatility?.value === rhsVolControl.targetVolatility?.value
            )
          }
          default:
            return true
        }
      }
      case 'protection': {
        const lhsProtection = lhs.protection
        const rhsProtection = rhs.protection
        const sameProtectionType = lhsProtection.type === rhsProtection.type
        if (!sameProtectionType) return false
        switch (lhs.protection.type) {
          case 'continuous': {
            const lhsContinuous = lhsProtection.continuous
            const rhsContinuous = rhsProtection.continuous
            return (
              lhsContinuous.protectionAsset?.assetId === rhsContinuous.protectionAsset?.assetId &&
              lhsContinuous.targetVolatility?.value === rhsContinuous.targetVolatility?.value &&
              lhsContinuous.protectionLevel?.value === rhsContinuous.protectionLevel?.value &&
              lhsContinuous.highWaterMarkFrequency === rhsContinuous.highWaterMarkFrequency &&
              lhsContinuous.leverageRatio.value === rhsContinuous.leverageRatio.value
            )
          }
          case 'term': {
            const lhsTerm = lhsProtection.term
            const rhsTerm = rhsProtection.term
            return (
              lhsTerm.protectionAsset?.assetId === rhsTerm.protectionAsset?.assetId &&
              lhsTerm.targetVolatility?.value === rhsTerm.targetVolatility?.value &&
              lhsTerm.protectionLevel?.value === rhsTerm.protectionLevel?.value &&
              lhsTerm.highWaterMarkFrequency === rhsTerm.highWaterMarkFrequency &&
              lhsTerm.leverageRatio.value === rhsTerm.leverageRatio.value &&
              lhsTerm.maturityDate?.value === rhsTerm.maturityDate?.value
            )
          }
          default:
            return true
        }
      }
      default:
        return true
    }
  else {
    return false
  }
}

export function getDisallowedStrategiesFromPendingState(
  pendingAccountState: ApiAccountBuilderState
): string[] {
  return getDisallowedStrategies(
    pendingAccountState.portfolio?.type,
    pendingAccountState.portfolio as ApiModelPortfolio,
    pendingAccountState.portfolio as ApiSelfSelectPortfolio,
    pendingAccountState.businessUnit
  )
}

export function getDisallowedStrategiesFromCurrentState(
  currentAccountState: ApiAccountState,
  businessUnit: ApiBusinessUnit
): string[] {
  return getDisallowedStrategies(
    currentAccountState?.portfolio?.type,
    currentAccountState?.portfolio as ApiModelPortfolio,
    currentAccountState?.portfolio as ApiSelfSelectPortfolio,
    businessUnit
  )
}

export function getDisallowedStrategiesFromSelectedState(
  selectedState: PortfolioStepState,
  businessUnit: ApiBusinessUnit
): string[] {
  return getDisallowedStrategies(
    // eslint-disable-next-line no-nested-ternary
    selectedState.portfolioType === 'ModelPortfolio'
      ? 'ModelPortfolio'
      : selectedState.portfolioType === 'SelfSelect'
      ? 'SelfSelect'
      : 'None',
    selectedState?.model?.portfolio as any,
    selectedState?.selfSelect?.portfolio,
    businessUnit
  )
}

function getDisallowedStrategies(
  portfolioType: ApiPortfolioType,
  portfolio: ApiModelPortfolio | ApiFundOfFundsPortfolio,
  selfSelectPortfolio: ApiSelfSelectPortfolio,
  businessUnit: ApiBusinessUnit
): string[] {
  let portfolioDisallowedStrategies = []
  if (portfolioType === 'ModelPortfolio' || portfolioType === 'FundOfFunds') {
    portfolioDisallowedStrategies = portfolio?.disallowedStrategies
  }
  if (portfolioType === 'SelfSelect') {
    const assets = selfSelectPortfolio.targetWeights.map(x => x.asset)
    portfolioDisallowedStrategies = getDisallowedStrategiesFromAssets(assets)
  }
  const businessUnitDisallowedStrategies = businessUnit?.disallowedStrategies
  return getMergedDisallowedStrategies(
    businessUnitDisallowedStrategies,
    portfolioDisallowedStrategies
  )
}

export function getMergedDisallowedStrategies(
  businessUnitDisallowedStrategies: string[],
  portfolioDisallowedStrategies: string[]
): string[] {
  const disallowedStrategies = new Set<string>()
  if (portfolioDisallowedStrategies) {
    portfolioDisallowedStrategies.forEach(item => disallowedStrategies.add(item))
  }
  if (businessUnitDisallowedStrategies) {
    businessUnitDisallowedStrategies.forEach(item => disallowedStrategies.add(item))
  }
  return Array.from(disallowedStrategies)
}

function getDisallowedStrategiesFromAssets(assets: any[]): string[] {
  const disallowedStrategies = new Set<string>()
  if (assets) {
    assets.map(x => x.disallowedStrategies.forEach(item => disallowedStrategies.add(item)))
  }
  return Array.from(disallowedStrategies)
}
